import React, { useContext, useEffect, useState } from "react";

import { Avatar, CardHeader } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import {
	getConnectionChannelColor,
	getConnectionChannelIcon,
} from "../ConnectionChannelIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	getStoredPrivacyCensorSettings,
	privacyCensorChangedEvent,
} from "../../utils/privacyCensor";

const getReadableTextColor = color => {
	const hex = (color || "#ffffff").replace("#", "");
	const normalized =
		hex.length === 3
			? hex.split("").map(char => `${char}${char}`).join("")
			: hex.padEnd(6, "f").slice(0, 6);
	const red = parseInt(normalized.slice(0, 2), 16);
	const green = parseInt(normalized.slice(2, 4), 16);
	const blue = parseInt(normalized.slice(4, 6), 16);
	const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

	return luminance > 150 ? "#243040" : "#ffffff";
};

const useStyles = makeStyles(theme => ({
	headerRoot: {
		minWidth: 0,
		width: "100%",
		[theme.breakpoints.down("sm")]: {
			paddingRight: 4,
		},
	},
	headerContent: {
		minWidth: 0,
	},
	subheaderBadges: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		minWidth: 0,
		overflow: "hidden",
		[theme.breakpoints.down("sm")]: {
			overflowX: "auto",
			overflowY: "hidden",
			flexWrap: "nowrap",
			maxWidth: "100%",
			paddingBottom: 2,
			touchAction: "pan-x",
			cursor: "grab",
			WebkitOverflowScrolling: "touch",
			scrollbarWidth: "thin",
		},
	},
	badge: {
		display: "inline-flex",
		alignItems: "center",
		flex: "0 0 auto",
		maxWidth: 150,
		height: "auto",
		minHeight: 24,
		padding: "2px 10px",
		borderRadius: 12,
		fontSize: "0.8125rem",
		fontWeight: 500,
		lineHeight: 1.4,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		border: "1px solid rgba(0, 0, 0, 0.08)",
		color: "#ffffff",
		backgroundColor: "#2576D2",
	},
	userBadge: {
		backgroundColor: "#455A64",
		gap: 6,
		paddingLeft: 4,
	},
	connectionBadge: {
		backgroundColor: "#148040",
		gap: 6,
		paddingLeft: 4,
	},
	badgeAvatar: {
		width: 20,
		height: 20,
		fontSize: 12,
	},
	connectionAvatar: {
		width: 20,
		height: 20,
		backgroundColor: "#ffffff",
	},
	connectionIcon: {
		width: 18,
		height: 18,
		display: "block",
	},
	badgeText: {
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	headerAction: {
		alignSelf: "center",
		marginTop: 0,
		marginRight: 0,
		flex: "0 0 auto",
	},
	censoredPhoto: {
		filter: "blur(10px)",
		transform: "scale(1.04)",
		borderRadius: "50%",
		overflow: "hidden",
	},
	censoredText: {
		padding: "1px 8px",
		filter: "blur(3px)",
		userSelect: "none",
	},
}));

const TicketInfo = ({ contact, ticket, onClick, action }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
		getStoredPrivacyCensorSettings(user?.id)
	);
	const queueColor = ticket.queue?.color || "#7C7C7C";
	const connectionChannel = ticket.connection?.channel || "whatsapp";
	const ConnectionIcon = getConnectionChannelIcon(connectionChannel);
	const connectionColor = getConnectionChannelColor(connectionChannel);

	useEffect(() => {
		setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
	}, [user?.id]);

	useEffect(() => {
		const handlePrivacyCensorChanged = event => {
			setPrivacyCensorSettings(
				event.detail || getStoredPrivacyCensorSettings(user?.id)
			);
		};

		window.addEventListener(
			privacyCensorChangedEvent,
			handlePrivacyCensorChanged
		);

		return () => {
			window.removeEventListener(
				privacyCensorChangedEvent,
				handlePrivacyCensorChanged
			);
		};
	}, [user?.id]);

	return (
		<CardHeader
			onClick={onClick}
			style={{ cursor: "pointer" }}
			classes={{
				root: classes.headerRoot,
				content: classes.headerContent,
				action: classes.headerAction,
			}}
			titleTypographyProps={{ noWrap: true }}
			avatar={
				<Avatar
					src={
						privacyCensorSettings.contactPhotos
							? undefined
							: contact.profilePicUrl
					}
					alt="contact_image"
					className={clsx({
						[classes.censoredPhoto]:
							privacyCensorSettings.contactPhotos,
					})}
				/>
			}
			action={action}
			title={
				<span
					className={clsx({
						[classes.censoredText]: privacyCensorSettings.contactNames,
					})}
				>
					{privacyCensorSettings.contactNames
						? `Contacto privado #${ticket.id}`
						: `${contact.name} #${ticket.id}`}
				</span>
			}
			subheader={
				<div
					className={classes.subheaderBadges}
					onClick={event => event.stopPropagation()}
					onMouseDown={event => event.stopPropagation()}
					onTouchStart={event => event.stopPropagation()}
				>
					{ticket.user?.name && (
						<span
							className={`${classes.badge} ${classes.userBadge}`}
							title="Usuario"
						>
							<Avatar
								src={ticket.user.profilePicUrl}
								className={classes.badgeAvatar}
							>
								{ticket.user.name?.[0]}
							</Avatar>
							<span className={classes.badgeText}>{ticket.user.name}</span>
						</span>
					)}
					{(ticket.connection?.name || ticket.connection?.number) && (
						<span
							className={`${classes.badge} ${classes.connectionBadge}`}
							title="Conexión que se está utilizando actualmente."
							style={{ backgroundColor: connectionColor }}
						>
							<Avatar className={classes.connectionAvatar}>
								<ConnectionIcon className={classes.connectionIcon} />
							</Avatar>
							<span className={classes.badgeText}>
								{ticket.connection.name || ticket.connection.number}
							</span>
						</span>
					)}
					{ticket.queue?.name && (
						<span
							className={classes.badge}
							title="Área"
							style={{
								backgroundColor: queueColor,
								color: getReadableTextColor(queueColor),
							}}
						>
							{ticket.queue.name}
						</span>
					)}
				</div>
			}
		/>
	);
};

export default TicketInfo;

